import styled, { keyframes } from "styled-components";

export const gradient = "linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%)";

export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const SidebarLayout = styled.div`
  display: flex;
  height: 100vh;
  width: 100%;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
    overflow: visible;
  }
`;

export const MainContentArea = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow-y: auto;

  @media (max-width: 768px) {
    height: auto;
    overflow-y: visible;
  }
`;

export const ContentWrap = styled.div`
  width: 100%;
  max-width: 1680px;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 24px 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  animation: ${fadeUp} 0.35s ease both;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const LoadingState = styled.div`
  text-align: center;
  padding: 120px 20px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  color: rgba(23, 23, 23, 0.4);
`;

export const TitleCard = styled.div`
  padding: 0;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const BackBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  border-radius: 10px;
  background: white;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.6);
  flex-shrink: 0;
  transition: background 0.2s;
  &:hover { background: #f5f7fa; }
`;

export const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  color: var(--mp-headingColor, #171717);
`;

export const PageSubtitle = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.65));
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: 8px;

  @media (max-width: 480px) {
    width: 100%;
    button {
      flex: 1;
      justify-content: center;
    }
  }
`;

const baseHeaderBtn = `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: 9px;
  font-family: Urbanist, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
`;

export const OutlineBtn = styled.button`
  ${baseHeaderBtn}
  border: 1.5px solid var(--mp-border, rgba(23, 23, 23, 0.12));
  background: var(--mp-cardBackground, white);
  color: var(--mp-headingColor, rgba(23, 23, 23, 0.65));
  &:hover { opacity: 0.85; }
`;

export const EditBtn = styled.button`
  ${baseHeaderBtn}
  border: none;
  background: var(--mp-heroBtnBg, ${gradient});
  color: var(--mp-heroBtnColor, white);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  &:hover { opacity: 0.9; }
`;

export const ProfileViewLayout = styled.div<{ $hasSidebar?: boolean }>`
  display: grid;
  grid-template-columns: ${p => p.$hasSidebar ? "1fr 340px" : "1fr"};
  gap: 32px;
  align-items: flex-start;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

export const LeftCard = styled.div`
  background: var(--mp-cardBackground, white);
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.07));
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

export const AvatarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

export const ProfilePicture = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid var(--mp-cardBackground, white);
  box-shadow: 0 4px 14px rgba(201, 77, 170, 0.22);
`;

export const AvatarCircle = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: rgba(201, 77, 170, 0.12);
  color: #c94daa;
  font-size: 38px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const DisplayName = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: var(--mp-headingColor, #171717);
  text-align: center;
`;

export const TypeBadge = styled.span<{ $type: string }>`
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  font-family: Montserrat, sans-serif;
  background: ${(p) =>
    p.$type === "studio" ? "rgba(124,77,255,0.15)" : "rgba(201,77,170,0.15)"};
  color: ${(p) => (p.$type === "studio" ? "#a78bfa" : "#c94daa")};
`;

export const VerifiedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 700;
  color: #16a34a;
  background: rgba(22, 163, 74, 0.12);
  padding: 3px 8px;
  border-radius: 20px;
`;

export const DetailsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-top: 1px solid var(--mp-border, rgba(23, 23, 23, 0.06));
  padding-top: 16px;
`;

export const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const IconWrap = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${gradient};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(201, 77, 170, 0.25);
  svg {
    stroke: white !important;
  }
`;

export const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

export const DetailLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 9px;
  font-weight: 700;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.5));
  text-transform: uppercase;
`;

export const DetailValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--mp-headingColor, #171717);
`;

export const RightCard = styled.div`
  background: var(--mp-cardBackground, white);
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.07));
  border-radius: 16px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

export const ViewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ViewSectionTitle = styled.h3`
  margin: 0;
  font-size: 13px;
  font-weight: 750;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.55));
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1.5px solid var(--mp-border, rgba(23, 23, 23, 0.06));
  padding-bottom: 6px;
`;

export const BioQuote = styled.p`
  margin: 0;
  font-size: 14px;
  color: var(--mp-headingColor, rgba(23, 23, 23, 0.85));
  line-height: 1.6;
  font-style: italic;
`;

export const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
`;

export const DetailGridCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--mp-bodyBackground, #f8f9fc);
  padding: 12px;
  border-radius: 10px;
  color: var(--mp-textColor, #171717);
  strong { font-size: 11px; font-family: Montserrat; display: block; opacity: 0.5; color: inherit; }
  span { font-size: 13px; font-weight: 700; color: inherit; }
`;

export const TagsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const TagChip = styled.span`
  background: rgba(201, 77, 170, 0.07);
  color: #c94daa;
  font-family: Urbanist, sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
`;

export const PortfolioGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

export const PortfolioLinkCard = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: var(--mp-bodyBackground, #f8f9fa);
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.06));
  border-radius: 10px;
  text-decoration: none;
  color: var(--mp-textColor, #171717);
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s;
  &:hover {
    background: var(--mp-cardBackground, white);
    border-color: #c94daa;
    box-shadow: 0 4px 12px rgba(201, 77, 170, 0.06);
    color: var(--mp-textColor, #171717);
  }
`;

export const FormCard = styled.form`
  background: var(--mp-cardBackground, white);
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.07));
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SectionLabel = styled.h3`
  margin: 0;
  font-size: 11px;
  font-weight: 800;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.55));
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--mp-border, rgba(23, 23, 23, 0.08));
`;

export const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

export const ThreeCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  @media (max-width: 700px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const Label = styled.label`
  font-size: 11px;
  font-weight: 700;
  color: var(--mp-headingColor, rgba(23, 23, 23, 0.75));
  font-family: Montserrat, sans-serif;
`;

const inputBase = `
  height: 40px;
  padding: 0 14px;
  border: 1.5px solid var(--mp-border, rgba(23,23,23,0.15));
  border-radius: 9px;
  font-size: 14px;
  font-family: Urbanist, sans-serif;
  color: var(--mp-headingColor, #171717);
  background: var(--mp-cardBackground, white);
  transition: border-color 0.15s;
  &:focus {
    outline: none;
    border-color: #c94daa66;
    box-shadow: 0 0 0 3px rgba(201,77,170,0.07);
  }
`;

export const Input = styled.input`${inputBase}`;
export const Select = styled.select`
  ${inputBase}
  cursor: pointer;
`;

export const Textarea = styled.textarea`
  padding: 12px 14px;
  border: 1.5px solid var(--mp-border, rgba(23,23,23,0.15));
  border-radius: 9px;
  font-size: 14px;
  font-family: Urbanist, sans-serif;
  color: var(--mp-headingColor, #171717);
  background: var(--mp-cardBackground, white);
  resize: vertical;
  min-height: 100px;
  &:focus {
    outline: none;
    border-color: #c94daa66;
    box-shadow: 0 0 0 3px rgba(201,77,170,0.07);
  }
`;

export const ImageUploadAreaEdit = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
`;

export const ImagePreviewEdit = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const PreviewImgEdit = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
`;

export const RemoveImgBtnEdit = styled.button`
  border: none;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  padding: 4px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: #ef4444; color: white; }
`;

export const UploadLabelEdit = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 14px;
  border: 1.5px dashed var(--mp-border, rgba(23, 23, 23, 0.2));
  border-radius: 9px;
  background: var(--mp-cardBackground, white);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  color: var(--mp-headingColor, rgba(23, 23, 23, 0.7));
  &:hover { opacity: 0.85; }
`;

export const PortfolioList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const PortfolioItemRow = styled.div`
  display: flex;
  align-items: center;
  background: var(--mp-cardBackground, #f8f9fa);
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.08));
  color: var(--mp-headingColor, #171717);
`;

export const RemovePortBtn = styled.button`
  margin-left: auto;
  border: none;
  background: none;
  color: var(--mp-heroSubColor, rgba(23,23,23,0.4));
  cursor: pointer;
  &:hover { color: #ef4444; }
`;

export const PortfolioAddBox = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const AddPortBtn = styled.button`
  height: 40px;
  padding: 0 18px;
  border: none;
  background: var(--mp-heroBtnBg, ${gradient});
  color: #ffffff !important;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 750;
  border-radius: 9px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    opacity: 0.94;
    transform: translateY(-1px);
  }
`;

export const FormFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 14px;
  padding-top: 24px;
  border-top: 1px solid var(--mp-border, rgba(255, 255, 255, 0.08));
`;

export const CancelBtn = styled.button`
  height: 42px;
  padding: 0 22px;
  border: 1.5px solid var(--mp-border, rgba(255, 255, 255, 0.15));
  border-radius: 10px;
  background: transparent;
  color: var(--mp-headingColor, #ffffff);
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: var(--mp-border, rgba(255, 255, 255, 0.08));
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const SubmitBtn = styled.button`
  height: 42px;
  padding: 0 26px;
  border: none;
  border-radius: 10px;
  background: var(--mp-heroBtnBg, ${gradient});
  color: #ffffff !important;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.3px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  svg {
    stroke: #ffffff !important;
  }

  &:hover {
    opacity: 0.94;
    transform: translateY(-1.5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const PhotoModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(23, 23, 23, 0.5);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

export const PhotoModalContent = styled.div`
  background: var(--mp-cardBackground, white);
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.08));
  color: var(--mp-headingColor, #171717);
  width: 100%;
  max-width: 340px;
  border-radius: 16px;
  padding: 24px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.25);
`;

export const CloseModalBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  border: none;
  background: var(--mp-border, rgba(23, 23, 23, 0.05));
  color: var(--mp-headingColor, #171717);
  cursor: pointer;
  padding: 6px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { opacity: 0.8; }
`;

export const ModalTitle = styled.h4`
  margin: 0 0 6px;
  font-family: Urbanist, sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: var(--mp-headingColor, #171717);
`;

export const OptionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.08));
  background: var(--mp-bodyBackground, #fdfdfd);
  color: var(--mp-textColor, #171717);
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: var(--mp-cardBackground, #f7f8fc); }
`;

export const CameraContainer = styled.div`
  width: 100%;
  height: 280px;
  border-radius: 10px;
  overflow: hidden;
  background: black;
`;

export const CameraVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const CameraControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
`;

export const CaptureBtn = styled.button`
  height: 40px;
  width: 100%;
  border: none;
  border-radius: 10px;
  background: ${gradient};
  color: white;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 750;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(201, 77, 170, 0.22);
`;

export const PublicToggleArea = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-right: 12px;
`;

export const ToggleLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.6);
`;

export const Toggle = styled.button<{ $on: boolean }>`
  width: 46px;
  height: 26px;
  border-radius: 100px;
  background: ${p => p.$on ? "var(--mp-heroBtnBg, " + gradient + ")" : "rgba(23, 23, 23, 0.15)"};
  border: none;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  transition: background 0.2s;
`;

export const ToggleKnob = styled.div<{ $on: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: white;
  position: absolute;
  top: 3px;
  left: ${p => p.$on ? "23px" : "3px"};
  transition: left 0.2s;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
`;
